import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import type { NodesResponse } from '@/stores/scope';
import { NodeMultiSelect } from '@/components/NodeMultiSelect';
import { EmployeeMultiSelect } from '@/components/EmployeeMultiSelect';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RELATION_OPTIONS, EMPLOYEE_FLAG_OPTIONS, VIEWER_PERMISSION_ACTION_OPTIONS, TARGET_NODE_RELATION_OPTIONS } from './labels';
import type { ConditionAttributeType, ConditionValue } from './types';

type EmploymentType = { type_id: string; code: string; label: string };
type EmployeeCategory = { category_id: string; code: string; label: string };
type PositionLevel = { position_level_id: string; code: string; label: string; sort_order: number };
type Skill = { skill_id: string; code: string; label: string };
type SkillLevel = { skill_level_id: string; code: string; label: string; sort_order: number };
type RoleProfile = { role_profile_id: string; code: string; label: string };
type EmployeeGrade = { grade_id: string; code: string; label: string; sort_order: number };

type ChipOption = { id: string; label: string };

/** タップ領域44px (モバイル) / 32px (デスクトップ) のトグルチップ一覧。
 *  雇用形態・社員区分・relation_to_viewer など選択肢が少ない master_ref/relation 向け
 *  (Select の components/ui/select.tsx は単一選択のため複数選択には使えない、§8-101 参照)。 */
function ChipToggleList({ options, selected, onToggle, emptyMessage, disabled }: {
  options: ChipOption[];
  selected: string[];
  onToggle: (id: string) => void;
  emptyMessage?: string;
  disabled?: boolean;
}) {
  if (options.length === 0) {
    return <p className="text-xs text-text-sub">{emptyMessage ?? '選択肢がありません'}</p>;
  }
  return (
    <div className="flex flex-wrap gap-1.5" role="group">
      {options.map((opt) => {
        const isSelected = selected.includes(opt.id);
        return (
          <button
            key={opt.id}
            type="button"
            aria-pressed={isSelected}
            disabled={disabled}
            onClick={() => onToggle(opt.id)}
            className={`px-2.5 min-h-[44px] sm:min-h-[32px] inline-flex items-center rounded-full text-xs border transition disabled:opacity-45 disabled:cursor-not-allowed ${
              isSelected
                // Design レビュー指摘: 選択状態インジケータは CTA (brand-red) と区別する
                // (InheritanceSourceBadge の 'self' 表現と同じ misespark-navy トークンに揃える)
                ? 'bg-misespark-navy text-white border-misespark-navy'
                : 'bg-white text-text-body border-surface-border hover:bg-surface-sunken'
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

type Props = {
  attributeType: ConditionAttributeType;
  operator: string;
  value: ConditionValue;
  onChange: (value: ConditionValue) => void;
  /** v0.4 TD-22: 条件集合の書込みは requireTenantWideManage (routes/condition-sets.ts) で
   *  全店舗スコープを要求するため、権限が無い閲覧者には読み取り専用で見せる。 */
  disabled?: boolean;
  /** viewer_permission 専用 (2026-09-18): value.feature はこの条件セットが属する
   *  usage_point.feature に固定する (自由記述させると feature 不一致で保存時に拒否される)。 */
  usagePointFeature?: string;
};

/** 属性型ごとに値入力UIを切り替える。§2-2 の value_json 形式 (master_ref/node_assignment は
 *  {ids:[...]}、relation は {relations:[...]}、skill は {skill_id, min_level_sort_order?}、
 *  employee_flag は {flag}、position_level の gte/lte は {ids:[閾値の1件], sort_order}
 *  それ以外の演算子は {ids:[...]}) に合わせて保存する。 */
export function ConditionValueEditor({ attributeType, operator, value, onChange, disabled, usagePointFeature }: Props) {
  const singleSelect = operator === 'eq';
  const isRelation = attributeType.value_kind === 'relation';
  const selectedIds = useMemo(
    () => (isRelation ? value.relations ?? [] : value.ids ?? []),
    [value, isRelation],
  );

  const setSelected = (ids: string[]) => {
    onChange(isRelation ? { relations: ids } : { ids });
  };

  const toggle = (id: string) => {
    if (singleSelect) {
      setSelected(selectedIds.includes(id) ? [] : [id]);
      return;
    }
    setSelected(selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]);
  };

  const { data: employmentTypes = [] } = useQuery<EmploymentType[]>({
    queryKey: ['employment-types'],
    queryFn: () => apiFetch<EmploymentType[]>('/employment-types'),
    enabled: attributeType.attribute_type_id === 'employment_type',
    staleTime: 5 * 60 * 1000,
  });
  const { data: employeeCategories = [] } = useQuery<EmployeeCategory[]>({
    queryKey: ['employee-categories'],
    queryFn: () => apiFetch<EmployeeCategory[]>('/employee-categories'),
    enabled: attributeType.attribute_type_id === 'employee_category',
    staleTime: 5 * 60 * 1000,
  });
  const { data: nodesData } = useQuery<NodesResponse>({
    queryKey: ['nodes'],
    queryFn: () => apiFetch<NodesResponse>('/nodes'),
    enabled: attributeType.attribute_type_id === 'node_assignment',
    staleTime: 5 * 60 * 1000,
  });
  const { data: positionLevels = [] } = useQuery<PositionLevel[]>({
    queryKey: ['position-levels'],
    queryFn: () => apiFetch<PositionLevel[]>('/masters/position-levels'),
    enabled: attributeType.attribute_type_id === 'position_level',
    staleTime: 5 * 60 * 1000,
  });
  const { data: skills = [] } = useQuery<Skill[]>({
    queryKey: ['skills'],
    queryFn: () => apiFetch<Skill[]>('/masters/skills'),
    enabled: attributeType.attribute_type_id === 'skill',
    staleTime: 5 * 60 * 1000,
  });
  const { data: skillLevels = [] } = useQuery<SkillLevel[]>({
    queryKey: ['skill-levels'],
    queryFn: () => apiFetch<SkillLevel[]>('/masters/skill-levels'),
    enabled: attributeType.attribute_type_id === 'skill' && operator === 'gte',
    staleTime: 5 * 60 * 1000,
  });
  const { data: roleProfiles = [] } = useQuery<RoleProfile[]>({
    queryKey: ['role-profiles'],
    queryFn: () => apiFetch<RoleProfile[]>('/masters/role-profiles'),
    enabled: attributeType.attribute_type_id === 'role_profile',
    staleTime: 5 * 60 * 1000,
  });
  const { data: employeeGrades = [] } = useQuery<EmployeeGrade[]>({
    queryKey: ['employee-grades'],
    queryFn: () => apiFetch<EmployeeGrade[]>('/masters/employee-grades'),
    enabled: attributeType.attribute_type_id === 'grade',
    staleTime: 5 * 60 * 1000,
  });

  if (attributeType.attribute_type_id === 'employment_type') {
    return (
      <ChipToggleList disabled={disabled}
        options={employmentTypes.map((t) => ({ id: t.type_id, label: t.label }))}
        selected={selectedIds}
        onToggle={toggle}
        emptyMessage="雇用形態が登録されていません (「雇用形態」タブから登録してください)"
      />
    );
  }
  if (attributeType.attribute_type_id === 'employee_category') {
    return (
      <ChipToggleList disabled={disabled}
        options={employeeCategories.map((cCat) => ({ id: cCat.category_id, label: cCat.label }))}
        selected={selectedIds}
        onToggle={toggle}
        emptyMessage="社員区分が登録されていません (「社員区分」タブから登録してください)"
      />
    );
  }
  if (attributeType.attribute_type_id === 'node_assignment') {
    return (
      <NodeMultiSelect
        disabled={disabled}
        nodes={nodesData?.roots ?? []}
        selectedIds={new Set(singleSelect ? selectedIds.slice(0, 1) : selectedIds)}
        onChange={(next) => {
          if (!singleSelect) { setSelected([...next]); return; }
          // eq (単一選択): 直近でトグルされた1件だけを残す
          const arr = [...next];
          const newOne = arr.find((id) => !selectedIds.includes(id));
          setSelected(newOne ? [newOne] : []);
        }}
      />
    );
  }
  if (attributeType.attribute_type_id === 'employee_identity') {
    // ⚠️ allowed_operators は in/not_in のみ (eq は無し、§2-1) なので singleSelect 分岐は不要
    return (
      <EmployeeMultiSelect
        disabled={disabled}
        selectedIds={new Set(selectedIds)}
        onChange={(next) => setSelected([...next])}
      />
    );
  }
  if (attributeType.attribute_type_id === 'target_node_relation') {
    // ⚠️ value_kind は 'target_node_relation' (relation_to_viewer の 'relation' とは別値、単数形の
    // {relation: string} のため isRelation 分岐には乗らない)。単一選択 (allowed_operators=["eq"]のみ)。
    const selected = value.relation ? [value.relation] : [];
    return (
      <ChipToggleList disabled={disabled}
        options={TARGET_NODE_RELATION_OPTIONS}
        selected={selected}
        onToggle={(id) => onChange(value.relation === id ? {} : { relation: id })}
      />
    );
  }
  if (isRelation) {
    return <ChipToggleList disabled={disabled} options={RELATION_OPTIONS} selected={selectedIds} onToggle={toggle} />;
  }
  if (attributeType.attribute_type_id === 'grade') {
    // gte/lte: sort_order の数値比較 (position_level と同型)。in/not_in/eq は ChipToggleList。
    if (operator === 'gte' || operator === 'lte') {
      if (employeeGrades.length === 0) {
        return <p className="text-xs text-text-sub">グレードが登録されていません (「グレード」タブから登録してください)</p>;
      }
      const selectedId = value.ids?.[0] ?? '';
      return (
        <Select
          disabled={disabled}
          value={selectedId}
          onValueChange={(v) => {
            const grade = employeeGrades.find((g) => g.grade_id === v);
            onChange(grade ? { ids: [grade.grade_id], sort_order: grade.sort_order } : {});
          }}
        >
          <SelectTrigger aria-label="グレード"><SelectValue placeholder="グレードを選択" /></SelectTrigger>
          <SelectContent>
            {employeeGrades.map((g) => (
              <SelectItem key={g.grade_id} value={g.grade_id}>{g.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }
    return (
      <ChipToggleList disabled={disabled}
        options={employeeGrades.map((g) => ({ id: g.grade_id, label: g.label }))}
        selected={selectedIds}
        onToggle={toggle}
        emptyMessage="グレードが登録されていません (「グレード」タブから登録してください)"
      />
    );
  }
  if (attributeType.attribute_type_id === 'position_level') {
    // gte/lte: sort_order の数値比較で評価するため、閾値1件 (id は表示用、sort_order が評価に使われる)
    // を選ぶ専用UI。in/not_in/eq は他の master_ref 属性型と同じ ChipToggleList を使う。
    if (operator === 'gte' || operator === 'lte') {
      if (positionLevels.length === 0) {
        return <p className="text-xs text-text-sub">役職・等級が登録されていません (「役職・等級」タブから登録してください)</p>;
      }
      const selectedId = value.ids?.[0] ?? '';
      return (
        <Select
          disabled={disabled}
          value={selectedId}
          onValueChange={(v) => {
            const level = positionLevels.find((l) => l.position_level_id === v);
            onChange(level ? { ids: [level.position_level_id], sort_order: level.sort_order } : {});
          }}
        >
          <SelectTrigger aria-label="役職・等級"><SelectValue placeholder="役職・等級を選択" /></SelectTrigger>
          <SelectContent>
            {positionLevels.map((l) => (
              <SelectItem key={l.position_level_id} value={l.position_level_id}>{l.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }
    return (
      <ChipToggleList disabled={disabled}
        options={positionLevels.map((l) => ({ id: l.position_level_id, label: l.label }))}
        selected={selectedIds}
        onToggle={toggle}
        emptyMessage="役職・等級が登録されていません (「役職・等級」タブから登録してください)"
      />
    );
  }
  if (attributeType.attribute_type_id === 'skill') {
    // eq: スキルの保有有無のみ判定 (レベル不問)。gte: スキル+最低習熟度の両方を選ぶ。
    return (
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="flex-1 min-w-0">
          <Select
            disabled={disabled}
            value={value.skill_id ?? ''}
            onValueChange={(v) => onChange({
              skill_id: v || undefined,
              min_level_sort_order: operator === 'gte' ? value.min_level_sort_order : undefined,
            })}
          >
            <SelectTrigger aria-label="スキル"><SelectValue placeholder="スキルを選択" /></SelectTrigger>
            <SelectContent>
              {skills.map((s) => (
                <SelectItem key={s.skill_id} value={s.skill_id}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {skills.length === 0 && (
            <p className="text-xs text-text-sub mt-1">スキルが登録されていません (「スキル」タブから登録してください)</p>
          )}
        </div>
        {operator === 'gte' && (
          <div className="flex-1 min-w-0">
            {/* ⚠️ skill_levels.sort_order は一意制約が無いため (code のみ一意)、Select の value は
             *  必ず skill_level_id を使う (sort_order を直接使うと重複時に選択が壊れる、QA指摘)。 */}
            <Select
              disabled={disabled}
              value={skillLevels.find((l) => l.sort_order === value.min_level_sort_order)?.skill_level_id ?? ''}
              onValueChange={(v) => {
                const level = skillLevels.find((l) => l.skill_level_id === v);
                onChange({ skill_id: value.skill_id, min_level_sort_order: level?.sort_order });
              }}
            >
              <SelectTrigger aria-label="最低習熟度"><SelectValue placeholder="最低習熟度を選択" /></SelectTrigger>
              <SelectContent>
                {skillLevels.map((l) => (
                  <SelectItem key={l.skill_level_id} value={l.skill_level_id}>{l.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {skillLevels.length === 0 && (
              <p className="text-xs text-text-sub mt-1">習熟度が登録されていません (「習熟度」タブから登録してください)</p>
            )}
          </div>
        )}
      </div>
    );
  }
  if (attributeType.attribute_type_id === 'role_profile') {
    return (
      <ChipToggleList disabled={disabled}
        options={roleProfiles.map((p) => ({ id: p.role_profile_id, label: p.label }))}
        selected={selectedIds}
        onToggle={toggle}
        emptyMessage="人材像が登録されていません (「人材像」タブから登録してください)"
      />
    );
  }
  if (attributeType.attribute_type_id === 'employee_flag') {
    const selectedFlag = value.flag ? [value.flag] : [];
    return (
      <ChipToggleList disabled={disabled}
        options={EMPLOYEE_FLAG_OPTIONS}
        selected={selectedFlag}
        onToggle={(id) => onChange(value.flag === id ? {} : { flag: id })}
      />
    );
  }
  if (attributeType.attribute_type_id === 'viewer_permission') {
    // ⚠️ feature は自由記述させない (usage_point.feature に固定、保存時検証と一致させる)。
    // scope は現状 'all_stores' 固定のみ対応 (own_store 等への拡張は需要が出てから、§6d)。
    if (!usagePointFeature) {
      return <p className="text-xs text-danger-text">この利用箇所の feature を解決できませんでした。</p>;
    }
    const selectedAction = value.action ? [value.action] : [];
    return (
      <div className="flex flex-col gap-1.5">
        <ChipToggleList disabled={disabled}
          options={VIEWER_PERMISSION_ACTION_OPTIONS}
          selected={selectedAction}
          onToggle={(id) => onChange(value.action === id
            ? {}
            : { feature: usagePointFeature, action: id, scope: 'all_stores' })}
        />
        <p className="text-xs text-text-sub">
          この利用箇所を全店舗スコープで選択した操作の権限として持つ閲覧者に表示します
          (社員に紐付いていない管理者アカウントも対象になります)。
        </p>
      </div>
    );
  }
  return <p className="text-xs text-text-sub">この属性型の入力UIは現在利用できません。</p>;
}
